/**
 * The portal's sign-in outcome: what the server decided about the signed-in person
 * (`GET /customer-api/session/customers`) turned into the page the portal lands on.
 *
 * Neither function here holds a rule. The server decided `state` (docs/features/customer-storefront/
 * 25_api_auth_and_staff_targets_p4_update_spec.md section 5.2); `outcomeFromSignedInCustomers` only
 * renames it into the page outcome, and `decidePortalLanding` is the portal's own table for what to do
 * with that outcome and the person's remembered choice (28_ section 5.2). Neither decides who may see
 * what: the server validates the chosen customer on every request.
 */

import type {
  PortalLinkedCustomer,
  PortalSignInOutcome,
} from "../data/customer-portal-access-types";
import type { SignedInCustomers } from "../data/customer-api-types";

/** The server's answer, as the page outcome. */
export function outcomeFromSignedInCustomers(
  response: SignedInCustomers,
): PortalSignInOutcome {
  const customers: PortalLinkedCustomer[] = (response.customers ?? []).map(
    (c) => ({
      customer_id: c.customer_id,
      customer_name: c.customer_name,
      has_customer_wide_view: c.has_customer_wide_view,
      can_see_spend: c.can_see_spend,
      can_order_on_account: c.can_order_on_account,
      is_blocked: c.is_blocked,
    }),
  );
  if (response.state === "no_access") return { kind: "no_access" };
  return {
    kind: response.state,
    person_name: response.person_name ?? null,
    customers,
  };
}

/** The pages a sign-in outcome can land on instead of the portal. */
export type PortalLandingPage =
  "welcome" | "choose_customer" | "no_access" | "customer_blocked";

export type PortalLanding =
  /**
   * Show this page; the portal itself stays closed. `blockedCustomerId` is set only on `customer_blocked`,
   * when the customer the person was working as (chosen, remembered, or the one the portal was just
   * using) has been blocked: the page names that customer instead of opening another one silently.
   */
  | { kind: "page"; page: PortalLandingPage; blockedCustomerId?: string }
  /** Go in. `customer` is who to work as; null when there is nothing to choose between (the old model serving). */
  | { kind: "open"; customer: PortalLinkedCustomer | null };

/** What the portal remembers about this person's choices, none of it authority. */
export interface PortalLandingMemory {
  /**
   * The customer chosen in this browser session. It survives a reload (it is kept for the tab), so a
   * person who did not ask to be remembered is still on the customer they were using after a refresh.
   */
  chosenCustomerId: string | null;
  /** The customer remembered on this device from an earlier visit. */
  rememberedCustomerId: string | null;
  /** The welcome page has been shown this session (the first data call records the visit server-side). */
  welcomeAcknowledged: boolean;
  /**
   * The customer the person was working as when the portal had to ask again (a refused request clears
   * the choice). Never used to open anyone: only to say that customer has been paused.
   */
  previousCustomerId?: string | null;
}

/**
 * Where a sign-in outcome sends the person. A remembered choice is used silently only while it is still
 * one of the person's customers and not blocked; one that is no longer linked is ignored, which sends a
 * person with several customers back to the chooser. A customer the person was working as who is now
 * blocked is never swapped for another one: they land on that customer's not-available page, which
 * offers the chooser (28_ section 5.2, Brad, 14 Oct 2026). The block is the server's `is_blocked`, never
 * inferred from a refused request.
 */
export function decidePortalLanding(
  outcome: PortalSignInOutcome,
  memory: PortalLandingMemory,
): PortalLanding {
  if (outcome.kind === "no_access") return { kind: "page", page: "no_access" };
  if (outcome.kind === "customer_blocked")
    return { kind: "page", page: "customer_blocked" };
  if (outcome.kind === "welcome" && !memory.welcomeAcknowledged)
    return { kind: "page", page: "welcome" };

  if (outcome.customers.length === 0) return { kind: "open", customer: null };
  const usable = outcome.customers.filter((c) => !c.is_blocked);
  if (usable.length === 0) return { kind: "page", page: "customer_blocked" };

  for (const preferredId of [
    memory.chosenCustomerId,
    memory.rememberedCustomerId,
  ]) {
    const preferred = outcome.customers.find(
      (c) => c.customer_id === preferredId,
    );
    if (preferred?.is_blocked) return blockedLanding(preferred);
    if (preferred) return { kind: "open", customer: preferred };
  }
  const previous = outcome.customers.find(
    (c) => c.customer_id === memory.previousCustomerId,
  );
  if (previous?.is_blocked) return blockedLanding(previous);
  if (usable.length === 1) return { kind: "open", customer: usable[0]! };
  return { kind: "page", page: "choose_customer" };
}

function blockedLanding(customer: PortalLinkedCustomer): PortalLanding {
  return {
    kind: "page",
    page: "customer_blocked",
    blockedCustomerId: customer.customer_id,
  };
}
